import { type Color4, Matrix, Mesh, MeshBuilder, TransformNode, Vector3 } from "@babylonjs/core";
import { CART, trackPose } from "@rivalwatch/shared";
import { glow, toon } from "../game/materials.ts";
import type { DressContext, MapLook, Owned, PayloadContext, PayloadView, TrackContext } from "./types.ts";

/** The objective's resting colour (state colours take over when it's pushed or held). */
export const NEUTRAL = "#EEE9DF";

/** Distant stacks and gantries, so a greybox arena doesn't float in a void. */
function industrialBackdrop({ scene, extent }: DressContext): Owned[] {
  const out: Mesh[] = [];
  const r0 = Math.max(90, extent + 30);
  for (let i = 0; i < 14; i++) {
    const a = (i / 14) * Math.PI * 2;
    const r = r0 + (i % 3) * 18;
    const h = 20 + ((i * 37) % 30);
    const stack = MeshBuilder.CreateCylinder(`stack${i}`, { diameterTop: 5, diameterBottom: 8, height: h, tessellation: 10 }, scene);
    stack.position.set(Math.cos(a) * r, h / 2 - 2, Math.sin(a) * r);
    stack.material = toon(scene, i % 2 ? "#5b6275" : "#6f6a62", { high: false });
    stack.isPickable = false;
    stack.freezeWorldMatrix();
    out.push(stack);
    if (i % 3 === 0) {
      const lamp = MeshBuilder.CreateSphere(`lamp${i}`, { diameter: 2.2, segments: 8 }, scene);
      lamp.position.set(stack.position.x, h - 1, stack.position.z);
      lamp.material = glow(scene, "#ffb347");
      lamp.isPickable = false;
      out.push(lamp);
    }
  }
  return out;
}

/** A ballasted bed with sleepers and two rails. */
export function sleeperTrack({ scene, track, own }: TrackContext) {
  const pts = track.points;
  const side = (i: number, off: number, y: number) => {
    const p = pts[i]!;
    const t = track.tangents[i]!;
    return new Vector3(p.x - t.z * off, p.y + y, p.z + t.x * off);
  };
  const bed = own(MeshBuilder.CreateRibbon("trackBed", { pathArray: [pts.map((_, i) => side(i, -1.3, 0.015)), pts.map((_, i) => side(i, 1.3, 0.015))], sideOrientation: Mesh.DOUBLESIDE }, scene));
  bed.material = toon(scene, "#3b3834", { high: false });
  bed.receiveShadows = true;
  bed.isPickable = false;
  const sleeper = own(MeshBuilder.CreateBox("sleeper", { width: 2.2, height: 0.1, depth: 0.28 }, scene));
  sleeper.material = toon(scene, "#5b4a3b", { high: false });
  sleeper.receiveShadows = true;
  const ties: number[] = [];
  for (let d = 0.45; d < track.length; d += 0.9) {
    const p = trackPose(track, d);
    ties.push(...Matrix.RotationY(Math.atan2(p.fx, p.fz)).multiply(Matrix.Translation(p.x, p.y + 0.05, p.z)).asArray());
  }
  sleeper.thinInstanceSetBuffer("matrix", new Float32Array(ties), 16);
  for (const off of [-0.72, 0.72]) {
    const rail = own(MeshBuilder.CreateTube("rail", { path: pts.map((_, i) => side(i, off, 0.14)), radius: 0.07, tessellation: 6 }, scene));
    rail.material = toon(scene, "#9aa1ab");
    rail.isPickable = false;
  }
}

/** Ink and shadows for a payload part. */
export function payloadPart(ctx: PayloadContext, root: TransformNode, m: Mesh, x: number, y: number, z: number, hex: string, edges = true, high = true) {
  m.parent = root;
  m.position.set(x, y, z);
  m.material = toon(ctx.scene, hex, { high });
  m.receiveShadows = true;
  m.isPickable = false;
  ctx.shadows.addShadowCaster(m);
  if (edges) inkEdges(m, ctx.ink);
  return m;
}

export function inkEdges(m: Mesh, ink: Color4, width = 4, epsilon = 0.95) {
  m.enableEdgesRendering(epsilon);
  m.edgesWidth = width;
  m.edgesColor = ink;
}

/** The generic payload: a flatbed chassis carrying a core, banded in the state colour. */
export function flatbedPayload(ctx: PayloadContext): PayloadView {
  const { scene } = ctx;
  const root = ctx.own(new TransformNode("payload", scene));
  const part = (m: Mesh, x: number, y: number, z: number, hex: string, edges = true) => payloadPart(ctx, root, m, x, y, z, hex, edges);
  part(MeshBuilder.CreateBox("payloadFrame", { width: 2.0, height: 0.32, depth: 3.2 }, scene), 0, 0.44, 0, "#30343d");
  part(MeshBuilder.CreateBox("payloadDeck", { width: CART.hw * 2, height: 0.36, depth: CART.hl * 2 }, scene), 0, CART.deck - 0.18, 0, "#c79a3e");
  for (const x of [-1.02, 1.02])
    for (const z of [-1.2, 1.2]) {
      const w = part(MeshBuilder.CreateCylinder("payloadWheel", { diameter: 0.62, height: 0.22, tessellation: 18 }, scene), x, 0.31, z, "#1f2229", false);
      w.rotation.z = Math.PI / 2;
    }
  part(MeshBuilder.CreateBox("payloadCore", { width: CART.coreHw * 2, height: CART.coreH, depth: CART.coreHl * 2 }, scene), 0, CART.deck + CART.coreH / 2, 0, "#3a4150");
  const glows: Mesh[] = [];
  const lit = (m: Mesh, x: number, y: number, z: number) => {
    m.parent = root;
    m.position.set(x, y, z);
    m.material = glow(scene, NEUTRAL);
    m.isPickable = false;
    glows.push(m);
    return m;
  };
  for (const y of [CART.deck + 0.28, CART.deck + CART.coreH - 0.26]) lit(MeshBuilder.CreateBox("payloadBand", { width: CART.coreHw * 2 + 0.04, height: 0.12, depth: CART.coreHl * 2 + 0.04 }, scene), 0, y, 0);
  for (const x of [-CART.hw, CART.hw]) lit(MeshBuilder.CreateBox("payloadStrip", { width: 0.07, height: 0.07, depth: CART.hl * 2 }, scene), x, CART.deck, 0);
  const orbHeight = CART.deck + CART.coreH + 0.38;
  const orb = lit(MeshBuilder.CreateSphere("payloadOrb", { diameter: 0.62, segments: 12 }, scene), 0, orbHeight, 0);
  return { root, glows, ring: pushRing(ctx, root), orb, orbHeight };
}

/** The push-radius ring every payload wears. */
export function pushRing({ scene }: PayloadContext, root: TransformNode) {
  const ring = MeshBuilder.CreateTorus("payloadRing", { diameter: CART.radius * 2, thickness: 0.09, tessellation: 72 }, scene);
  ring.parent = root;
  ring.position.y = 0.06;
  ring.material = glow(scene, NEUTRAL, 0.5);
  ring.isPickable = false;
  return ring;
}

/** The greybox maps' look: a pale industrial sky with smokestacks on the horizon. */
export const DEFAULT_LOOK: MapLook = {
  sky: { top: "#8fb3c9" },
  // Rivals-sized maps: haze begins to tell past ~100 m.
  fog: { color: "#8fb3c9", density: 0.00275 },
  hemi: { intensity: 0.8, sky: "#ffffff", ground: "#4a4f63", dir: [0.2, 1, 0.1] },
  sun: { dir: [-0.45, -1, -0.3], intensity: 0.95, color: "#fff1d6" },
  shadowDarkness: 0.35,
  ink: { color: "#171a24", width: 4 },
  glow: 0.55,
  dress: industrialBackdrop,
};
